import { motion } from "framer-motion";
import { Button } from "../../ui/button";
import { TFunction } from "i18next";
import { FC } from "react";

interface Props {
  onSuggestionClick: (_action: string) => void;
  t: TFunction;
}

const ThreadWelcomeSuggestions: FC<Props> = ({ onSuggestionClick, t }) => {
  // Split a suggestion into two lines: a bolder title and a lighter label
  const splitSuggestionIntoTitleAndLabel = (suggestion: string): { title: string; label: string } => {
    const words = suggestion
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (words.length <= 1) {
      return { title: suggestion, label: "" };
    }
    const midpoint = Math.ceil(words.length / 2);
    const title = words.slice(0, midpoint).join(" ");
    const label = words.slice(midpoint).join(" ");
    return { title, label };
  };

  // Fetch up to four translated starter prompts; ensure an array shape
  const translatedSuggestions = (t("documentchat.empty.suggestions", { returnObjects: true, defaultValue: [] }) as unknown) as string[];
  const suggestions: string[] = Array.isArray(translatedSuggestions) && translatedSuggestions.length > 0 ? translatedSuggestions : [];

  return (
    <div className="grid w-full gap-2 sm:grid-cols-2">
      {suggestions.slice(0, 4).map((suggestion, index) => {
        const { title, label } = splitSuggestionIntoTitleAndLabel(suggestion);
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.05 * index }}
            key={`suggested-action-${title}-${index}`}
            className="nth-[n+3]:hidden sm:nth-[n+3]:block"
          >
            <Button
              variant="ghost"
              className="dark:hover:bg-accent/60 h-auto w-full flex-1 flex-wrap items-start justify-start gap-1 rounded-xl border px-4 py-3.5 text-left text-sm sm:flex-col whitespace-break-spaces"
              aria-label={suggestion}
              onClick={() => { onSuggestionClick(suggestion); }}
            >
              <span className="font-medium">{title}</span>
              <p className="text-muted-foreground">{label}</p>
            </Button>
          </motion.div>
        );
      })}
    </div>
  );
};

export default ThreadWelcomeSuggestions;
